import "./index.scss";
import "./assets/stylesheets/fff.scss";
import "./assets/stylesheets/footer.scss";

import * as React from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import * as ReactDom from "react-dom";

import { App, loader as appLoader } from "./components/app";
import { RootStore } from "./stores/root-store";
import Error from "./pages/error/error";
import Login, { action as loginAction, loader as loginLoader } from "./pages/login/login";
import Logout, { loader as logoutLoader } from "./pages/logout/logout";
import Contacts, { loader as contactsLoader } from "./pages/contacts/index/contacts";
import ContactShow, { loader as contactShowLoader } from "./pages/contacts/show/contact-show";
import ContactEdit, {
    loader as contactEditLoader,
    action as contactEditAction
} from "./pages/contacts/edit/contact-edit";
import { action as contactDeleteAction } from "./pages/contacts/delete/contact-delete";
import ContactNew, { action as contactNewAction } from "./pages/contacts/new/contact-new";
import Projects, { loader as projectsLoader } from "./pages/projects/index/projects";
import ProjectNew, {
    action as projectNewAction,
    loader as projectNewLoader
} from "./pages/projects/new/project-new";
import ProjectShow, {
    action as projectShowAction,
    loader as projectShowLoader
} from "./pages/projects/show/project-show";
import { action as projectDeleteAction } from "./pages/projects/delete/project-delete";
import ProjectEdit, {
    loader as projectEditLoader,
    action as projectEditAction
} from "./pages/projects/edit/project-edit";
import CreationAreaEdit, {
    loader as creationAreaEditLoader,
    action as creationAreaEditAction
} from "./pages/areas/edit/creation-area-edit";
import MoreMaps from "./pages/more-maps/more-maps";
import { AppConfiguration } from "./core/config/app-configuration";
import { MapViewerConfiguration } from "./config/map-viewer-configuration";

import { JsonSerializer } from "typescript-json-serializer";
import esriRequest from "@arcgis/core/request";
import { AppState } from "./stores/app-mobx";

import RequestResponse = __esri.RequestResponse;
import MatomoTracker from "@datapunt/matomo-tracker-js";
import { UiState } from "./core/ui-state";
import CompensationNew, {
    action as compensationNewAction,
    loader as compensationNewLoader,
} from "./pages/compensations/new/compensation-new";
import CompensationEdit, {
    action as compensationEditAction,
    loader as compensationEditLoader,
} from "./pages/compensations/edit/compensation-edit";
import CompensationDelete, {
    action as compensationDeleteAction,
} from "./pages/compensations/delete/compensation-delete";
import CompensationNewSelectCreationProject, {
    loader as compensationNewSelectCreationProjectLoader
} from "./pages/compensations/new/compensation-new-select-creation-project";
import Reports, { action as reportsAction, loader as reportsLoader } from "./pages/reports/index/reports";
import { reportRegistry } from "./pages/reports/show/report-registry";
import { Constants } from "./globals/constants";
import Help from "./pages/help/help";

const loadConfig = (url: string, file: string): Promise<RequestResponse> => {
    const path = url + "/" + file;
    return esriRequest(path, {
        responseType: "json"
    });
};

// TODO: auf APP_ROOT umstellen bzw. auf unterschiedliche URL pro Zielumgebung (Target)
// TODO: In JSON die Pfade auf Icons ersetzen je nach Target
const loadConfigs = async (url: string) =>
    // use typescript dynamic import to load the config json files
    await Promise.all([
        loadConfig(url, "core.json"),
        loadConfig(url, "mapviewer.json")
    ]);

// Map to the country class
try {
    // __CONFIG_ROOT__ is replaced by Webpack build process
    // see config/environments
    // noinspection UnnecessaryLocalVariableJS
    const configRoot = __CONFIG_ROOT__;
    loadConfigs(configRoot).then(([appJson, mapviewerJson]) => {
        const defaultSerializer = new JsonSerializer();
        const appConfig = defaultSerializer.deserializeObject<AppConfiguration>(appJson.data, AppConfiguration);

        // deserialize as specific configuration type, but pass it to the App as generic base class type
        const mapViewerConfig =
            defaultSerializer.deserializeObject(mapviewerJson.data, MapViewerConfiguration);
        if (appConfig && mapViewerConfig) {
            const tracker =
                (mapViewerConfig.tracker !== undefined) ? new MatomoTracker(mapViewerConfig.tracker) : undefined;
            const appStore = new AppState(mapViewerConfig, tracker);

            const rootStore = new RootStore(mapViewerConfig);
            const uiStore = new UiState();

            const router = createBrowserRouter(
                [
                    {
                        path: "/",
                        element:
                            <App
                                config={appConfig}
                                moduleConfig={mapViewerConfig}
                                appStore={appStore}
                                authenticationStore={rootStore.authenticationStore}
                                uiStore={uiStore}
                                rootStore={rootStore}
                            />,

                        errorElement: <Error/>,
                        loader: async () =>
                            await appLoader(rootStore.authenticationStore),
                        children: [
                            {
                                errorElement: <Error/>,
                                children: [
                                    {
                                        index: true,
                                        element: <Navigate to="/projekte"/>,
                                    },
                                    {
                                        path: "abmelden",
                                        element: <Logout/>,
                                        loader: async () => await logoutLoader(rootStore.authenticationStore),
                                    },
                                    {
                                        path: "projekte",
                                        element: <Projects/>,
                                        loader: async ({ request }) =>
                                            await projectsLoader(
                                                request,
                                                rootStore,
                                                appStore
                                            ),
                                    },
                                    {
                                        path: "projekte/:projectCategory/:projectId/",
                                        element: <ProjectShow/>,
                                        action: async ({ request, params }) =>
                                            await projectShowAction(
                                                request,
                                                params,
                                                rootStore
                                            ),
                                        loader: async ({ params }) =>
                                            await projectShowLoader(
                                                params,
                                                rootStore,
                                                appStore
                                            ),
                                        children: [
                                            {
                                                path: "kompensation/neu",
                                                element: <CompensationNewSelectCreationProject/>,
                                                loader: async ({ request, params }) =>
                                                    await compensationNewSelectCreationProjectLoader(
                                                        request,
                                                        params,
                                                        rootStore
                                                    )
                                            },
                                            {
                                                path: "kompensation/neu/:creationProjectId",
                                                element: <CompensationNew/>,
                                                action: async ({ request }) =>
                                                    await compensationNewAction(
                                                        request,
                                                        rootStore
                                                    ),
                                                loader: async ({ params }) =>
                                                    await compensationNewLoader(
                                                        params,
                                                        rootStore
                                                    ),
                                            },
                                            {
                                                path: "kompensation/:compensationId/aendern",
                                                element: <CompensationEdit/>,
                                                action: async ({ request }) =>
                                                    await compensationEditAction(
                                                        request,
                                                        rootStore
                                                    ),
                                                loader: async ({ params }) =>
                                                    await compensationEditLoader(
                                                        params,
                                                        rootStore
                                                    ),
                                            },
                                            {
                                                path: "kompensation/:compensationId/loeschen",
                                                element: <CompensationDelete/>,
                                                action: async ({ params }) =>
                                                    await compensationDeleteAction(
                                                        params,
                                                        rootStore
                                                    ),
                                            },
                                            {
                                                path: `flaechen/${Constants.projectCategoryCreation}/:areaId/aendern`,
                                                element: <CreationAreaEdit/>,
                                                loader: async ({ params }) =>
                                                    await creationAreaEditLoader(
                                                        params,
                                                        rootStore
                                                    ),
                                                action: async ({ request }) =>
                                                    await creationAreaEditAction(
                                                        request,
                                                        rootStore
                                                    ),
                                            },
                                        ]
                                    },
                                    {
                                        path: "projekte/neu",
                                        element: <ProjectNew/>,
                                        action: async ({ request }) =>
                                            await projectNewAction(
                                                request,
                                                rootStore
                                            ),
                                        loader: async () =>
                                            await projectNewLoader(rootStore),
                                    },
                                    {
                                        path: "projekte/:projectCategory/:projectId/aendern",
                                        element: <ProjectEdit/>,
                                        action: async ({ request }) =>
                                            await projectEditAction(
                                                request,
                                                rootStore
                                            ),
                                        loader: async ({ params }) =>
                                            await projectEditLoader(
                                                params,
                                                rootStore
                                            ),
                                    },
                                    {
                                        path: "projekte/:projectCategory/:projectId/loeschen",
                                        action: async ({ params }) =>
                                            await projectDeleteAction(
                                                params,
                                                rootStore
                                            ),
                                    },
                                    {
                                        path: "reports",
                                        element: <Reports/>,
                                        action: async ({ request }) => await reportsAction(request),
                                        loader: async ({ request }) => await reportsLoader(request, appStore, rootStore),
                                        children: reportRegistry.map(r => {
                                            return {
                                                path: r.key,
                                                element: <r.component/>,
                                                loader: async () => await r.loader(rootStore, appStore)
                                            };
                                        })

                                    },
                                    {
                                        path: "kontakte",
                                        element: <Contacts/>,
                                        loader: async ({ request }) =>
                                            await contactsLoader(request, rootStore, appStore),
                                    },
                                    {
                                        path: "kontakte/:contactId",
                                        element: <ContactShow/>,
                                        loader: async ({ params }) =>
                                            await contactShowLoader(params, rootStore),
                                    },
                                    {
                                        path: "kontakte/neu",
                                        element: <ContactNew/>,
                                        action: async ({ request }) =>
                                            await contactNewAction(request, rootStore),
                                    },
                                    {
                                        path: "kontakte/:contactId/aendern",
                                        element: <ContactEdit/>,
                                        action: async ({ request }) =>
                                            await contactEditAction(request, rootStore),
                                        loader: async ({ params }) =>
                                            await contactEditLoader(params, rootStore),
                                    },
                                    {
                                        path: "kontakte/:contactId/loeschen",
                                        action: async ({ params }) =>
                                            await contactDeleteAction(params, rootStore),
                                    },
                                    {
                                        path: "weitere-karten",
                                        element: <MoreMaps/>,
                                    },
                                    {
                                        path: "hilfe",
                                        element: <Help/>,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: "/anmelden",
                        element: <Login/>,
                        loader: async () => await loginLoader(rootStore, uiStore, appConfig, mapViewerConfig),
                        action: async ({ request }) =>
                            await loginAction({
                                request, rootStore
                            }),
                    },
                ],
                { basename: appConfig.basename }
            );

            ReactDom.render(
                <RouterProvider router={router}/>,
                document.getElementById("app-root")
            );
        }
    }).catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
    });
} catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
}
